"use client";

import { useEffect, useId } from "react";
import type { ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/shared/utils/cn";
import styles from "./download-page-layout.module.css";
import type {
  DownloadLink,
  DownloadResourcePost,
} from "./download-types";

/** 下载弹窗暂不展示 Official 与 MediaFile，仅保留当前需要的下载入口。 */
const DOWNLOAD_LINK_KEYS = [
  "other_google",
  "other",
] as const;

export type DownloadDialogState = {
  links: DownloadLink[];
  title: string;
};

type ResourcePostCardProps = {
  downloadAction: string;
  downloadLinkLabels: Record<string, string>;
  onDownload: (post: DownloadResourcePost, links: DownloadLink[]) => void;
  post: DownloadResourcePost;
};

/** 渲染单个下载资源卡片。 */
export function ResourcePostCard({
  downloadAction,
  downloadLinkLabels,
  onDownload,
  post,
}: ResourcePostCardProps) {
  const links = getDownloadLinks(post.more, downloadLinkLabels);
  const mobileHeight = post.post_excerpt
    ? "min-h-[222px]"
    : "min-h-[162px]";

  return (
    <article
      className={cn(
        "flex flex-col justify-center gap-2.5 rounded-[5px] bg-white px-[30px] py-7",
        styles.downloadResourceCompactCard,
        mobileHeight,
      )}
    >
      <ResourcePostCardCopy post={post} />
      <ResourceDownloadButton
        label={downloadAction}
        onClick={() => onDownload(post, links)}
      />
    </article>
  );
}

/** 渲染资源卡片的标题和摘要。 */
function ResourcePostCardCopy({ post }: { post: DownloadResourcePost }) {
  return (
    <div className={cn("min-w-0", styles.downloadResourceCompactCardCopy)}>
      <h2
        className={cn(
          "break-words text-[20px] font-bold leading-[22px] tracking-[-0.01px] text-black",
          styles.downloadResourceCompactCardTitle,
        )}
        title={post.post_title}
      >
        {post.post_title}
      </h2>
      {post.post_excerpt ? (
        <p
          className={cn(
            "mt-2.5 max-w-[560px] break-words text-sm font-medium leading-[22px] tracking-[-0.01px] text-[#666666]",
            styles.downloadResourceCompactCardExcerpt,
          )}
          title={post.post_excerpt}
        >
          {post.post_excerpt}
        </p>
      ) : null}
    </div>
  );
}

/** 渲染资源卡片下载按钮。 */
function ResourceDownloadButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className={cn(
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6600] focus-visible:ring-offset-2",
        styles.downloadResourceCompactCardButton,
      )}
      onClick={onClick}
      type="button"
    >
      <DownloadResourceIcon />
      {label}
    </button>
  );
}

/** 渲染下载云图标。 */
function DownloadResourceIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      viewBox="0 0 20 16"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.5002 12.1924H10.8462V6.78404C10.8462 6.68582 10.7658 6.60547 10.6676 6.60547H9.32835C9.23013 6.60547 9.14977 6.68582 9.14977 6.78404V12.1924H7.50022C7.35067 12.1924 7.26808 12.3643 7.3596 12.4804L9.8596 15.6433C9.93103 15.7348 10.0694 15.7348 10.1408 15.6433L12.6408 12.4804C12.7324 12.3643 12.6498 12.1924 12.5002 12.1924Z"
        fill="currentColor"
      />
      <path
        d="M16.683 4.61376C15.6607 1.91738 13.0558 0 10.0045 0C6.95313 0 4.34821 1.91514 3.32589 4.61152C1.41295 5.11375 0 6.85702 0 8.92841C0 11.3949 1.99777 13.3926 4.46205 13.3926H5.35714C5.45536 13.3926 5.53571 13.3123 5.53571 13.214V11.8748C5.53571 11.7766 5.45536 11.6962 5.35714 11.6962H4.46205C3.70982 11.6962 3.00223 11.3971 2.47545 10.8547C1.95089 10.3145 1.67188 9.58688 1.69643 8.83243C1.71652 8.24316 1.91741 7.68959 2.28125 7.22308C2.65402 6.74765 3.17634 6.40167 3.7567 6.24766L4.60268 6.02668L4.91295 5.20973C5.10491 4.70081 5.37277 4.22537 5.70982 3.79457C6.04241 3.36601 6.4375 2.99325 6.87946 2.68076C7.79688 2.03568 8.87723 1.69417 10.0045 1.69417C11.1317 1.69417 12.2121 2.03568 13.1295 2.68076C13.5737 2.99325 13.9665 3.36824 14.2991 3.79457C14.6362 4.22537 14.904 4.70304 15.096 5.20973L15.404 6.02445L16.2478 6.24766C17.4576 6.57354 18.3036 7.67397 18.3036 8.92841C18.3036 9.66724 18.0156 10.3637 17.4933 10.886C16.971 11.4083 16.2768 11.6962 15.5379 11.6962H14.6429C14.5446 11.6962 14.4643 11.7766 14.4643 11.8748V13.214C14.4643 13.3123 14.5446 13.3926 14.6429 13.3926H15.5379C18.0022 13.3926 20 11.3949 20 8.92841C20 6.85925 18.5915 5.11821 16.683 4.61376Z"
        fill="currentColor"
      />
    </svg>
  );
}

type DownloadLinksDialogProps = {
  closeLabel: string;
  links: DownloadLink[];
  onClose: () => void;
  title: string;
};

/** 渲染多下载地址选择弹窗。 */
export function DownloadLinksDialog({
  closeLabel,
  links,
  onClose,
  title,
}: DownloadLinksDialogProps) {
  const titleId = useId();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 p-4"
      onMouseDown={onClose}
    >
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className="relative w-full max-w-max rounded-[12px] bg-white px-8 pb-9 pt-[52px]"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <DialogCloseButton label={closeLabel} onClick={onClose} />
        <h2
          className="mx-auto max-w-[480px] break-words text-center text-[20px] font-bold leading-[22px] text-black"
          id={titleId}
        >
          {title}
        </h2>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          {links.map((link) => <DownloadLinkOption key={link.key} link={link} />)}
        </div>
      </div>
    </div>
  );
}

/** 渲染下载弹窗关闭按钮。 */
function DialogCloseButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      aria-label={label}
      className="absolute right-3 top-2 inline-flex size-8 items-center justify-center rounded-[5px] text-[#666666] transition hover:bg-[#f3f3f5] hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6600]"
      onClick={onClick}
      type="button"
    >
      <X className="size-6" />
    </button>
  );
}

/** 渲染单个下载地址选项。 */
function DownloadLinkOption({ link }: { link: DownloadLink }) {
  const className =
    "inline-flex h-[42px] w-[150px] items-center justify-center rounded-[5px] px-5 text-center text-base text-white";
  if (!link.isAvailable) {
    return (
      <button className={cn(className, "cursor-not-allowed bg-[#cccccc]")} disabled type="button">
        <span className="min-w-0 truncate">{link.label}</span>
      </button>
    );
  }
  return (
    <a
      className={cn(
        className,
        "bg-[#ff6600] transition hover:bg-[#e85c00] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6600] focus-visible:ring-offset-2",
      )}
      href={link.href}
      rel="noreferrer"
      target="_blank"
    >
      <span className="min-w-0 truncate">{link.label}</span>
    </a>
  );
}

/** 渲染资源区通用空状态。 */
export function EmptyState({
  compact = false,
  icon,
  message,
}: {
  compact?: boolean;
  icon: ReactNode;
  message: string;
}) {
  return (
    <div
      className={cn(
        "mt-6 grid min-h-[150px] place-items-center rounded-[12px] border border-dashed border-black/15 bg-white px-4 py-10 text-center text-[#777777]",
        compact && styles.downloadResourceCategoryEmpty,
      )}
    >
      <div className={cn("grid justify-items-center gap-3", compact && "grid-flow-col items-center justify-items-start")}>
        <div className={cn("grid size-12 place-items-center rounded-full bg-[#f4f5f6] text-[#999999]", compact && "size-8")}>
          {icon}
        </div>
        <p className="text-sm">{message}</p>
      </div>
    </div>
  );
}

/** 从资源 more 字段构建固定下载地址选项。 */
function getDownloadLinks(
  more: unknown,
  labelMap: Record<string, string>,
): DownloadLink[] {
  const value = parseMoreObject(more);
  return DOWNLOAD_LINK_KEYS.map((key) => {
    const rawHref = value?.[key];
    const isAvailable =
      typeof rawHref === "string" && rawHref.trim().length > 0;
    return {
      href: isAvailable ? rawHref.trim() : "",
      isAvailable,
      key,
      label: labelMap[key] ?? key,
    };
  });
}

/** 将 more 字段解析成普通对象。 */
function parseMoreObject(more: unknown): Record<string, unknown> | null {
  if (typeof more === "string") {
    try {
      return parseMoreObject(JSON.parse(more));
    } catch {
      return null;
    }
  }
  if (!more || Array.isArray(more) || typeof more !== "object") return null;
  return more as Record<string, unknown>;
}
